import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../components";
import { fetchAiSetup } from "../connectAi";
import {
  buildStarterApiIntegrationPayload,
  getStarterAccountConnections,
  getStarterApiKeyChoices,
  getStarterSubscriptionChoices,
  getStarterVisibleSubscriptionProviders,
  hasCompleteStarterApiValues,
  type StarterAccountConnectionId,
  type StarterApiKeyProvider,
  type StarterSubscriptionProvider,
} from "../connectCatalog";
import { fetchSetupProfile, saveSetupProfile, type SetupAnswers } from "../setupProfile";
import {
  DEFAULT_AGENT_NAME,
  resolveAgentLabel,
  type PublicAiMode,
} from "../../shared/consumer-language";
import type { AiSetupContract } from "../../shared/connect-ai";
import {
  ACCOUNT_CHOICES,
  AI_CHOICES,
  BUDGET_CHOICES,
  FOCUS_CHOICES,
  GUIDED_TOUR_STEPS,
  METHODOLOGY_PROJECT_CAPACITY_CHOICES,
  METHODOLOGY_REVIEW_CADENCE_CHOICES,
  METHODOLOGY_TASKING_CHOICES,
  SETUP_STEPS,
  getBudgetFollowUp,
  getFocusFollowUp,
  type SetupStepId,
  type WorkspaceTourSurface,
} from "./onboarding/registry";

type MethodologyTaskingStyle = "batch" | "interleave" | "mixed";
type MethodologyProjectCapacity = "1-3" | "4-7" | "8+";
type MethodologyReviewCadence = "yes_routinely" | "sometimes" | "no";

interface MethodologyDraftAnswers {
  taskingStyle: MethodologyTaskingStyle | null;
  projectCapacity: MethodologyProjectCapacity | null;
  weeklyReviewCadence: MethodologyReviewCadence | null;
}

async function saveMethodologyAnswers(answers: {
  taskingStyle: MethodologyTaskingStyle;
  projectCapacity: MethodologyProjectCapacity;
  weeklyReviewCadence: MethodologyReviewCadence;
}): Promise<void> {
  const res = await fetch("/api/methodology-profile/answers", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(answers),
  });
  if (!res.ok) {
    throw new Error("Could not save your working-style answers");
  }
}

function methodologyTaskingLabel(value: MethodologyTaskingStyle): string {
  if (value === "batch") return "Batch tasks";
  if (value === "interleave") return "Interleave";
  return "A bit of both";
}

function methodologyCadenceLabel(value: MethodologyReviewCadence): string {
  if (value === "yes_routinely") return "Weekly review: yes";
  if (value === "sometimes") return "Weekly review: sometimes";
  return "No weekly review";
}

interface OnboardingMessage {
  id: string;
  role: "agent" | "user";
  content: string;
}

interface ServiceStatus {
  state: "idle" | "working" | "connected" | "error";
  detail?: string;
}

interface SubscriptionConnectResult {
  flowId?: string;
  flowType: "device_code" | "token_paste" | "redirect";
  authUrl?: string;
  userCode?: string;
  instructions?: string;
  expiresIn?: number;
  success?: boolean;
}

interface IntegrationTestResponse {
  ok?: boolean;
  error?: string;
  message?: string;
}

interface SanitizedProfile {
  profileId: string;
  provider: string;
}

interface SubscriptionProviderResponse {
  profiles?: SanitizedProfile[];
  availableProviders?: StarterSubscriptionProvider[];
}

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

interface ConversationalOnboardingProps {
  onComplete: () => void;
  onAgentNameChange?: (name: string) => void;
  initialAgentName?: string;
  onOpenSettings?: (target?: { sectionId?: string; pageId?: string }) => void;
  onOpenWorkspaceSurface?: (surface: WorkspaceTourSurface | "settings") => void;
  blocker?: { reason: string; message: string } | null;
}

const DEFAULT_ANSWERS: SetupAnswers = {
  agentName: DEFAULT_AGENT_NAME,
  dayOneFocus: null,
  aiPreference: "workspace_default",
  aiMode: "workspace_default",
  connectedAccounts: [],
  budgetPreference: null,
  limitedModeConfirmed: false,
};

const ACCOUNT_SOURCE_MAP: Partial<Record<StarterAccountConnectionId, string>> = {
  "operator-email": "gmail",
  "operator-calendar": "google-calendar",
  "agent-calendar": "google-calendar-agent",
};

function buildMessage(role: "agent" | "user", content: string): OnboardingMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role,
    content,
  };
}

function sanitizeName(value: string): string {
  return value.trim().replace(/\s+/g, " ").slice(0, 48);
}

function getSetupStepIndex(stepId: string | null | undefined): number {
  if (!stepId) return 0;
  const index = SETUP_STEPS.findIndex((step) => step.id === stepId);
  return index >= 0 ? index : 0;
}

function getTourIndex(stepId: string | null | undefined): number {
  if (!stepId) return 0;
  const index = GUIDED_TOUR_STEPS.findIndex((step) => step.id === stepId);
  return index >= 0 ? index : 0;
}

function useAutoScroll<T extends HTMLElement>(): React.RefObject<T> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  });
  return ref;
}

async function fetchSubscriptions(): Promise<SubscriptionProviderResponse> {
  const res = await fetch("/api/subscriptions");
  if (!res.ok) throw new Error("Could not load subscription options");
  return res.json();
}

async function connectSubscription(
  provider: string,
  token?: string,
): Promise<SubscriptionConnectResult> {
  const res = await fetch("/api/subscriptions/connect", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ provider, token }),
  });
  const body = (await res.json().catch(() => ({}))) as SubscriptionConnectResult & {
    message?: string;
    error?: string;
  };
  if (!res.ok) throw new Error(body.message || body.error || "Could not connect that subscription");
  return body;
}

async function completeSubscriptionFlow(flowId: string): Promise<void> {
  const res = await fetch(`/api/subscriptions/complete/${encodeURIComponent(flowId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  if (!res.ok) throw new Error(body.message || body.error || "That sign-in is not finished yet");
}

async function saveApiKey(providerId: string, payload: Record<string, unknown>): Promise<void> {
  const saveRes = await fetch(`/api/integrations/${encodeURIComponent(providerId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!saveRes.ok) {
    throw new Error(await readIntegrationActionError(saveRes, "Could not save that API key"));
  }

  const testRes = await fetch(`/api/integrations/${encodeURIComponent(providerId)}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const testBody = (await testRes.json().catch(() => ({}))) as IntegrationTestResponse;
  if (!testRes.ok || testBody.ok === false) {
    throw new Error(
      testBody.error ||
        testBody.message ||
        (await readIntegrationActionError(
          testRes,
          "Saved the key, but the connection check failed",
        )),
    );
  }
}

async function readIntegrationActionError(response: Response, fallback: string): Promise<string> {
  const body = (await response
    .clone()
    .json()
    .catch(() => ({}))) as ApiErrorResponse;
  if (body.error === "step_up_required") {
    return "Your security verification expired. Sign in again, then try saving this key one more time.";
  }
  return body.message || body.error || fallback;
}

async function startOauthConnection(
  sourceId: string,
): Promise<{ flowId: string; authUrl: string }> {
  const res = await fetch(`/api/data-sources/${encodeURIComponent(sourceId)}/oauth/start`, {
    method: "POST",
  });
  const body = (await res.json().catch(() => ({}))) as {
    flowId?: string;
    authUrl?: string;
    error?: string;
  };
  if (!res.ok || !body.flowId || !body.authUrl) {
    throw new Error(body.error || "Could not start that sign-in");
  }
  return { flowId: body.flowId, authUrl: body.authUrl };
}

async function pollOauthConnection(
  sourceId: string,
  flowId: string,
  onStatus?: (detail: string) => void,
): Promise<void> {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 2_000));
    const res = await fetch(
      `/api/data-sources/${encodeURIComponent(sourceId)}/oauth/status/${encodeURIComponent(flowId)}`,
    );
    const body = (await res.json().catch(() => ({}))) as {
      status?: string;
      message?: string | null;
      error?: string | null;
    };
    if (!res.ok) continue;
    if (body.status === "connected") return;
    if (body.status === "error") throw new Error(body.error || "That sign-in did not finish");
    onStatus?.(body.message || "Waiting for sign-in to finish...");
  }
  throw new Error("That sign-in timed out");
}

async function saveAgentEmailConfiguration(payload: {
  service: string;
  fromAddress: string;
  key: string;
}): Promise<void> {
  const res = await fetch("/api/integrations/agent-email", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      key: payload.key,
      service: payload.service,
      metadata: {
        fromAddress: payload.fromAddress,
        service: payload.service,
      },
    }),
  });
  if (!res.ok) throw new Error("Could not save agent email");
}

function ChoiceButton({
  label,
  description,
  active = false,
  onClick,
}: {
  label: string;
  description?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl px-4 py-3 text-left transition-all"
      style={{
        border: active ? "1px solid var(--c-primary)" : "1px solid var(--c-border)",
        backgroundColor: active ? "var(--c-primary-subtle)" : "var(--c-surface)",
      }}
    >
      <div className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
        {label}
      </div>
      {description && (
        <div className="mt-1 text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {description}
        </div>
      )}
    </button>
  );
}

function AgentBubble({
  label,
  showLabel = true,
  children,
}: {
  label: string;
  showLabel?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex gap-3">
      {showLabel ? (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-1"
          style={{ backgroundColor: "var(--c-primary-subtle)", color: "var(--c-primary)" }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="7" cy="5" r="2.5" />
            <path d="M3 12c0-2.2 1.8-4 4-4s4 1.8 4 4" />
          </svg>
        </div>
      ) : (
        <div className="w-7 shrink-0" aria-hidden />
      )}
      <div className="flex-1 min-w-0">
        {showLabel && (
          <div
            className="text-[10px] font-semibold uppercase mb-1"
            style={{ color: "var(--c-text-faint)" }}
          >
            {label}
          </div>
        )}
        <div className="text-sm" style={{ color: "var(--c-text)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ConversationalOnboarding({
  onComplete,
  onAgentNameChange,
  initialAgentName,
  onOpenSettings,
  onOpenWorkspaceSurface,
  blocker = null,
}: ConversationalOnboardingProps): React.ReactElement {
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [phase, setPhase] = useState<"setup" | "tour" | "complete">("setup");
  const [setupStepIndex, setSetupStepIndex] = useState(0);
  const [tourIndex, setTourIndex] = useState(0);
  const [answers, setAnswers] = useState<SetupAnswers>({
    ...DEFAULT_ANSWERS,
    agentName: sanitizeName(initialAgentName ?? "") || DEFAULT_AGENT_NAME,
  });
  const [agentNameInput, setAgentNameInput] = useState("");
  const [focusInput, setFocusInput] = useState("");
  const [currentAiMode, setCurrentAiMode] = useState<PublicAiMode>("workspace_default");
  const [aiSetup, setAiSetup] = useState<AiSetupContract | null>(null);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<
    StarterSubscriptionProvider[]
  >(getStarterSubscriptionChoices());
  const [selectedSubscriptionProvider, setSelectedSubscriptionProvider] =
    useState<StarterSubscriptionProvider | null>(null);
  const [subscriptionToken, setSubscriptionToken] = useState("");
  const [subscriptionFlow, setSubscriptionFlow] = useState<SubscriptionConnectResult | null>(null);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [selectedApiProvider, setSelectedApiProvider] = useState<StarterApiKeyProvider | null>(
    null,
  );
  const [apiFieldValues, setApiFieldValues] = useState<Record<string, string>>({});
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [apiKeyError, setApiKeyError] = useState("");
  const [accountChoice, setAccountChoice] = useState<"connect" | "later" | null>(null);
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, ServiceStatus>>({});
  const [expandedAccount, setExpandedAccount] = useState<StarterAccountConnectionId | null>(null);
  const [serviceError, setServiceError] = useState("");
  const [agentEmailService, setAgentEmailService] = useState("resend");
  const [agentEmailAddress, setAgentEmailAddress] = useState("");
  const [agentEmailKey, setAgentEmailKey] = useState("");
  const [agentEmailBusy, setAgentEmailBusy] = useState(false);
  const [budgetSelection, setBudgetSelection] = useState<string | null>(null);
  const [methodologyDraft, setMethodologyDraft] = useState<MethodologyDraftAnswers>({
    taskingStyle: null,
    projectCapacity: null,
    weeklyReviewCadence: null,
  });
  const [methodologyBusy, setMethodologyBusy] = useState(false);
  const [methodologyError, setMethodologyError] = useState("");
  const [finishing, setFinishing] = useState(false);
  const promptedKey = useRef<string | null>(null);
  const scrollRef = useAutoScroll<HTMLDivElement>();

  const agentDisplayName = resolveAgentLabel(answers.agentName);
  const currentSetupStep = SETUP_STEPS[setupStepIndex] ?? SETUP_STEPS[0];
  const currentTourStep = GUIDED_TOUR_STEPS[tourIndex] ?? GUIDED_TOUR_STEPS[0];
  const connectedAccounts = useMemo(
    () =>
      getStarterAccountConnections()
        .filter((card) => serviceStatuses[card.id]?.state === "connected")
        .map((card) => card.id),
    [serviceStatuses],
  );

  const appendMessage = useCallback((role: "agent" | "user", content: string) => {
    setMessages((previous) => [...previous, buildMessage(role, content)]);
  }, []);

  const persistProfile = useCallback(
    async ({
      nextPhase,
      nextAnswers,
      nextSetupStepId,
      nextTourStepId,
      nextCompleted = false,
    }: {
      nextPhase: "setup" | "tour" | "complete";
      nextAnswers?: SetupAnswers;
      nextSetupStepId?: SetupStepId;
      nextTourStepId?: string | null;
      nextCompleted?: boolean;
    }) => {
      const mergedAnswers = nextAnswers ?? answers;
      await saveSetupProfile({
        phase: nextPhase,
        currentStep:
          nextPhase === "setup"
            ? (nextSetupStepId ?? currentSetupStep.id)
            : nextPhase === "tour"
              ? (nextTourStepId ?? currentTourStep.id)
              : "complete",
        completed: nextCompleted,
        completedAt: nextCompleted ? new Date().toISOString() : null,
        agentName: mergedAnswers.agentName,
        answers: {
          ...mergedAnswers,
          connectedAccounts,
        },
        tourProgress: {
          currentStep: nextPhase === "tour" ? (nextTourStepId ?? currentTourStep.id) : null,
          completedSteps: GUIDED_TOUR_STEPS.slice(0, tourIndex)
            .map((step) => step.id)
            .concat(nextPhase === "complete" ? GUIDED_TOUR_STEPS.map((step) => step.id) : []),
          skippedSteps: [],
        },
      });
    },
    [answers, connectedAccounts, currentSetupStep.id, currentTourStep.id, tourIndex],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await fetchSetupProfile();
        if (cancelled) return;
        setPhase(profile.phase);
        setAnswers(profile.answers);
        setCurrentAiMode(profile.answers.aiPreference ?? profile.answers.aiMode);
        setBudgetSelection(profile.answers.budgetPreference);
        setSetupStepIndex(getSetupStepIndex(profile.currentStep));
        setTourIndex(getTourIndex(profile.tourProgress.currentStep));
        if (profile.answers.connectedAccounts.length > 0) {
          setAccountChoice("connect");
          setServiceStatuses(
            Object.fromEntries(
              profile.answers.connectedAccounts.map((connectionId) => [
                connectionId,
                { state: "connected", detail: "Connected" },
              ]),
            ),
          );
        }
        if (profile.answers.agentName) {
          onAgentNameChange?.(profile.answers.agentName);
        }
      } catch {
        if (!cancelled) {
          setAnswers((previous) => ({
            ...previous,
            agentName: sanitizeName(initialAgentName ?? "") || DEFAULT_AGENT_NAME,
          }));
        }
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialAgentName, onAgentNameChange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const ai = await fetchAiSetup();
        if (cancelled) return;
        setAiSetup(ai);
        setCurrentAiMode(ai.preferredSource);
      } catch {
        if (!cancelled) {
          setAiSetup(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchSubscriptions();
        if (cancelled) return;
        setAvailableSubscriptions(
          getStarterVisibleSubscriptionProviders(
            response.availableProviders ?? getStarterSubscriptionChoices(),
          ),
        );
      } catch {
        if (!cancelled) {
          setAvailableSubscriptions(getStarterSubscriptionChoices());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadingProfile) return;
    const key = `${phase}:${phase === "setup" ? currentSetupStep.id : currentTourStep.id}`;
    if (promptedKey.current === key) return;
    promptedKey.current = key;

    if (phase === "setup") {
      if (currentSetupStep.id === "ai") {
        appendMessage(
          "agent",
          aiSetup?.requiresConnection
            ? "Want to add your own AI now? You can keep going without it, but things will stay limited until you connect one."
            : "Your workspace is ready. If you want, you can also add your own subscription or API key now.",
        );
        return;
      }
      appendMessage("agent", currentSetupStep.prompt);
      return;
    }
    if (phase === "tour") {
      appendMessage("agent", `${currentTourStep.title}: ${currentTourStep.body}`);
      return;
    }
    appendMessage(
      "agent",
      "You’re all set. Start with a real task and I’ll help shape the workspace from there.",
    );
  }, [
    appendMessage,
    currentSetupStep.id,
    currentSetupStep.prompt,
    currentTourStep.body,
    currentTourStep.id,
    currentTourStep.title,
    aiSetup?.requiresConnection,
    loadingProfile,
    phase,
  ]);

  const goToNextSetupStep = useCallback(
    async (nextAnswers: SetupAnswers, assistantReply?: string) => {
      setAnswers(nextAnswers);
      if (assistantReply) appendMessage("agent", assistantReply);

      const nextIndex = setupStepIndex + 1;
      if (nextIndex >= SETUP_STEPS.length) {
        setPhase("tour");
        setTourIndex(0);
        await persistProfile({
          nextPhase: "tour",
          nextAnswers,
          nextTourStepId: GUIDED_TOUR_STEPS[0]?.id ?? null,
        });
        return;
      }

      setSetupStepIndex(nextIndex);
      await persistProfile({
        nextPhase: "setup",
        nextAnswers,
        nextSetupStepId: SETUP_STEPS[nextIndex].id,
      });
    },
    [appendMessage, persistProfile, setupStepIndex],
  );

  const handleNameSubmit = useCallback(async () => {
    const nextName = sanitizeName(agentNameInput) || DEFAULT_AGENT_NAME;
    appendMessage("user", nextName === DEFAULT_AGENT_NAME ? "Use the default name" : nextName);
    onAgentNameChange?.(nextName);
    const nextAnswers = { ...answers, agentName: nextName };
    await goToNextSetupStep(
      nextAnswers,
      nextName === DEFAULT_AGENT_NAME
        ? "Okay. I’ll use the default name for now."
        : `Nice. I’ll go by ${nextName}.`,
    );
  }, [agentNameInput, answers, appendMessage, goToNextSetupStep, onAgentNameChange]);

  const handleFocusChoice = useCallback(
    async (focusId: string, label: string, customText?: string) => {
      const value = customText?.trim() || label;
      appendMessage("user", value);
      const nextAnswers = { ...answers, dayOneFocus: value };
      await goToNextSetupStep(nextAnswers, getFocusFollowUp(focusId, customText));
    },
    [answers, appendMessage, goToNextSetupStep],
  );

  const handleManagedAi = useCallback(async () => {
    appendMessage("user", aiSetup?.requiresConnection ? "Skip for now" : "Later");
    const nextAnswers = {
      ...answers,
      aiPreference: "workspace_default" as PublicAiMode,
      aiMode: "workspace_default" as PublicAiMode,
      limitedModeConfirmed: false,
    };
    setCurrentAiMode("workspace_default");
    setSelectedSubscriptionProvider(null);
    setSelectedApiProvider(null);
    await goToNextSetupStep(
      nextAnswers,
      aiSetup?.requiresConnection
        ? "Okay. We can keep going for now, but things will stay limited until you connect your own AI."
        : "Okay. We can keep moving, and you can connect your own AI later.",
    );
  }, [aiSetup?.requiresConnection, answers, appendMessage, goToNextSetupStep]);

  const handleSubscriptionMode = useCallback(() => {
    setCurrentAiMode((previous) => {
      if (previous !== "byo_subscription") {
        appendMessage("user", "Use my own subscription");
      }
      return "byo_subscription";
    });
    setSelectedApiProvider(null);
    setApiKeyError("");
  }, [appendMessage]);

  const handleApiMode = useCallback(() => {
    setCurrentAiMode((previous) => {
      if (previous !== "byo_api") {
        appendMessage("user", "Use my own API key");
      }
      return "byo_api";
    });
    setSelectedSubscriptionProvider(null);
    setSubscriptionError("");
  }, [appendMessage]);

  const connectSelectedSubscription = useCallback(async () => {
    if (!selectedSubscriptionProvider) return;
    setSubscriptionBusy(true);
    setSubscriptionError("");
    try {
      const result = await connectSubscription(
        selectedSubscriptionProvider.id,
        selectedSubscriptionProvider.flowType === "token_paste"
          ? subscriptionToken.trim()
          : undefined,
      );
      if (result.success) {
        const nextAnswers = {
          ...answers,
          aiPreference: "byo_subscription" as PublicAiMode,
          aiMode: "byo_subscription" as PublicAiMode,
          limitedModeConfirmed: false,
        };
        await goToNextSetupStep(
          nextAnswers,
          `${selectedSubscriptionProvider.label} is connected. I’ll use that first.`,
        );
        return;
      }
      setSubscriptionFlow(result);
      if (result.authUrl) {
        window.open(result.authUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      setSubscriptionError(
        error instanceof Error ? error.message : "Could not connect that subscription",
      );
    } finally {
      setSubscriptionBusy(false);
    }
  }, [answers, goToNextSetupStep, selectedSubscriptionProvider, subscriptionToken]);

  const finishSubscriptionFlow = useCallback(async () => {
    if (!selectedSubscriptionProvider || !subscriptionFlow?.flowId) return;
    setSubscriptionBusy(true);
    setSubscriptionError("");
    try {
      await completeSubscriptionFlow(subscriptionFlow.flowId);
      const nextAnswers = {
        ...answers,
        aiPreference: "byo_subscription" as PublicAiMode,
        aiMode: "byo_subscription" as PublicAiMode,
        limitedModeConfirmed: false,
      };
      await goToNextSetupStep(
        nextAnswers,
        `${selectedSubscriptionProvider.label} is connected. I’ll use that first.`,
      );
    } catch (error) {
      setSubscriptionError(
        error instanceof Error ? error.message : "That sign-in did not finish cleanly",
      );
    } finally {
      setSubscriptionBusy(false);
    }
  }, [answers, goToNextSetupStep, selectedSubscriptionProvider, subscriptionFlow?.flowId]);

  const saveSelectedApiKey = useCallback(async () => {
    if (!selectedApiProvider || !hasCompleteStarterApiValues(selectedApiProvider, apiFieldValues)) {
      return;
    }
    setApiKeyBusy(true);
    setApiKeyError("");
    try {
      await saveApiKey(
        selectedApiProvider.integrationId,
        buildStarterApiIntegrationPayload(selectedApiProvider, apiFieldValues),
      );
      const nextAnswers = {
        ...answers,
        aiPreference: "byo_api" as PublicAiMode,
        aiMode: "byo_api" as PublicAiMode,
        limitedModeConfirmed: false,
      };
      await goToNextSetupStep(
        nextAnswers,
        `${selectedApiProvider.label} is connected. I’ll use that key first.`,
      );
    } catch (error) {
      setApiKeyError(error instanceof Error ? error.message : "Could not save that API key");
    } finally {
      setApiKeyBusy(false);
    }
  }, [answers, apiFieldValues, goToNextSetupStep, selectedApiProvider]);

  const keepIncludedAiForNow = useCallback(async () => {
    appendMessage("user", aiSetup?.requiresConnection ? "Skip for now" : "Later");
    const nextAnswers = {
      ...answers,
      aiPreference: "workspace_default" as PublicAiMode,
      aiMode: "workspace_default" as PublicAiMode,
      limitedModeConfirmed: Boolean(aiSetup?.requiresConnection),
    };
    setCurrentAiMode("workspace_default");
    setSelectedSubscriptionProvider(null);
    setSelectedApiProvider(null);
    await goToNextSetupStep(
      nextAnswers,
      aiSetup?.requiresConnection
        ? "No problem. We’ll keep going for now, but things will stay limited until you connect your own AI."
        : "No problem. You can connect your own AI later.",
    );
  }, [aiSetup?.requiresConnection, answers, appendMessage, goToNextSetupStep]);

  const updateServiceStatus = useCallback((serviceId: string, status: ServiceStatus) => {
    setServiceStatuses((previous) => ({ ...previous, [serviceId]: status }));
  }, []);

  const connectAccount = useCallback(
    async (cardId: StarterAccountConnectionId) => {
      const sourceId = ACCOUNT_SOURCE_MAP[cardId];
      if (!sourceId) return;
      setServiceError("");
      updateServiceStatus(cardId, { state: "working", detail: "Opening sign-in..." });
      try {
        const start = await startOauthConnection(sourceId);
        window.open(start.authUrl, "_blank", "noopener,noreferrer");
        await pollOauthConnection(sourceId, start.flowId, (detail) => {
          updateServiceStatus(cardId, { state: "working", detail });
        });
        updateServiceStatus(cardId, { state: "connected", detail: "Connected" });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Connection failed";
        updateServiceStatus(cardId, { state: "error", detail: message });
        setServiceError(message);
      }
    },
    [updateServiceStatus],
  );

  const saveAgentEmail = useCallback(async () => {
    setAgentEmailBusy(true);
    setServiceError("");
    try {
      await saveAgentEmailConfiguration({
        service: agentEmailService,
        fromAddress: agentEmailAddress.trim(),
        key: agentEmailKey.trim(),
      });
      updateServiceStatus("agent-email", {
        state: "connected",
        detail: agentEmailAddress.trim() || "Ready",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not save agent email";
      updateServiceStatus("agent-email", { state: "error", detail: message });
      setServiceError(message);
    } finally {
      setAgentEmailBusy(false);
    }
  }, [agentEmailAddress, agentEmailKey, agentEmailService, updateServiceStatus]);

  const handleAccountChoice = useCallback(
    async (choiceId: "connect" | "later") => {
      setAccountChoice(choiceId);
      appendMessage("user", choiceId === "connect" ? "Connect now" : "Later");
      if (choiceId === "later") {
        const nextAnswers = { ...answers, connectedAccounts: [] };
        await goToNextSetupStep(
          nextAnswers,
          "No problem. You can connect email or calendar later in Settings.",
        );
      }
    },
    [answers, appendMessage, goToNextSetupStep],
  );

  const continueAfterAccounts = useCallback(async () => {
    appendMessage("user", "Continue");
    const nextAnswers = { ...answers, connectedAccounts };
    await goToNextSetupStep(
      nextAnswers,
      connectedAccounts.length > 0
        ? "Great. I saved those connections."
        : "Okay. You can come back to these connections later.",
    );
  }, [answers, appendMessage, connectedAccounts, goToNextSetupStep]);

  const handleBudgetChoice = useCallback(
    async (choiceId: string, label: string) => {
      appendMessage("user", label);
      setBudgetSelection(choiceId);
      const nextAnswers = { ...answers, budgetPreference: choiceId };
      await goToNextSetupStep(nextAnswers, getBudgetFollowUp(choiceId));
    },
    [answers, appendMessage, goToNextSetupStep],
  );

  const submitMethodologyAnswers = useCallback(async () => {
    const { taskingStyle, projectCapacity, weeklyReviewCadence } = methodologyDraft;
    if (!taskingStyle || !projectCapacity || !weeklyReviewCadence) {
      setMethodologyError("Please answer all three questions before continuing.");
      return;
    }
    setMethodologyBusy(true);
    setMethodologyError("");
    try {
      await saveMethodologyAnswers({ taskingStyle, projectCapacity, weeklyReviewCadence });
      appendMessage(
        "user",
        `${methodologyTaskingLabel(taskingStyle)} · ${projectCapacity} projects · ${methodologyCadenceLabel(weeklyReviewCadence)}`,
      );
      await goToNextSetupStep(
        answers,
        weeklyReviewCadence === "no"
          ? "Got it. I'll skip auto-suggesting a weekly plan."
          : "Got it. I'll keep that in mind when I plan and check in with you.",
      );
    } catch (error) {
      setMethodologyError(error instanceof Error ? error.message : "Could not save your answers");
    } finally {
      setMethodologyBusy(false);
    }
  }, [answers, appendMessage, goToNextSetupStep, methodologyDraft]);

  const advanceTour = useCallback(async () => {
    const step = GUIDED_TOUR_STEPS[tourIndex];
    appendMessage("user", step.actionLabel);

    const nextIndex = tourIndex + 1;
    if (step.surface) {
      if (step.surface === "settings") {
        onOpenWorkspaceSurface?.("settings");
        onOpenSettings?.({ sectionId: "connect", pageId: "ai" });
      } else {
        onOpenWorkspaceSurface?.(step.surface);
      }
    }

    if (nextIndex >= GUIDED_TOUR_STEPS.length) {
      setPhase("complete");
      await persistProfile({
        nextPhase: "complete",
        nextAnswers: answers,
        nextCompleted: false,
      });
      return;
    }

    setTourIndex(nextIndex);
    await persistProfile({
      nextPhase: "tour",
      nextAnswers: answers,
      nextTourStepId: GUIDED_TOUR_STEPS[nextIndex].id,
    });
  }, [answers, appendMessage, onOpenSettings, onOpenWorkspaceSurface, persistProfile, tourIndex]);

  const finishOnboarding = useCallback(async () => {
    setFinishing(true);
    try {
      await fetch("/api/onboarding/complete", { method: "POST" });
      await persistProfile({
        nextPhase: "complete",
        nextAnswers: answers,
        nextCompleted: true,
      });
    } catch {
      // Best effort.
    } finally {
      setFinishing(false);
    }
    onComplete();
  }, [answers, onComplete, persistProfile]);

  if (loadingProfile) {
    return (
      <div className="max-w-3xl mx-auto py-10 text-sm" style={{ color: "var(--c-text-secondary)" }}>
        Loading setup...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {blocker && (
        <div
          className="rounded-2xl px-4 py-3"
          style={{
            border: "1px solid var(--c-warning)",
            backgroundColor: "rgba(234, 179, 8, 0.08)",
          }}
        >
          <div
            className="text-[11px] font-semibold uppercase mb-1"
            style={{ color: "var(--c-warning)" }}
          >
            Setup needs attention
          </div>
          <p className="text-sm" style={{ color: "var(--c-text)" }}>
            {blocker.message}
          </p>
        </div>
      )}

      {messages.map((message, idx) => {
        if (message.role === "user") {
          return (
            <div key={message.id} className="flex justify-end">
              <div
                className="rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[80%]"
                style={{ backgroundColor: "var(--c-primary)", color: "var(--c-primary-text)" }}
              >
                {message.content}
              </div>
            </div>
          );
        }
        // Agent message — only show label/avatar on first of a consecutive agent run.
        const prev = idx > 0 ? messages[idx - 1] : null;
        const isFirstInGroup = !prev || prev.role !== "agent";
        return (
          <AgentBubble key={message.id} label={agentDisplayName} showLabel={isFirstInGroup}>
            {message.content}
          </AgentBubble>
        );
      })}

      {phase === "setup" && (
        <div className="pl-4 md:pl-10 space-y-4">
          {currentSetupStep.id === "name" && (
            <div className="max-w-lg space-y-3">
              <div className="flex gap-2">
                <input
                  value={agentNameInput}
                  onChange={(event) => setAgentNameInput(event.target.value)}
                  placeholder="Your agent"
                  className="flex-1 text-sm rounded-xl px-3 py-2.5"
                  style={{
                    border: "1px solid var(--c-border)",
                    backgroundColor: "var(--c-surface)",
                    color: "var(--c-text)",
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void handleNameSubmit();
                    }
                  }}
                />
                <button
                  onClick={() => void handleNameSubmit()}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium"
                  style={{ backgroundColor: "var(--c-primary)", color: "var(--c-primary-text)" }}
                >
                  Save name
                </button>
              </div>
              <button
                onClick={() => {
                  appendMessage("user", "Use the default name");
                  onAgentNameChange?.(DEFAULT_AGENT_NAME);
                  void goToNextSetupStep(
                    { ...answers, agentName: DEFAULT_AGENT_NAME },
                    "Okay. I’ll use the default name for now.",
                  );
                }}
                className="text-left px-3 py-2 rounded-xl text-xs transition-colors"
                style={{ color: "var(--c-text-faint)" }}
              >
                Use the default name
              </button>
            </div>
          )}

          {currentSetupStep.id === "focus" && (
            <div className="max-w-xl space-y-3">
              <div className="grid gap-2">
                {FOCUS_CHOICES.map((choice) => (
                  <ChoiceButton
                    key={choice.id}
                    label={choice.label}
                    description={choice.description}
                    onClick={() => void handleFocusChoice(choice.id, choice.label)}
                  />
                ))}
              </div>
              <div className="space-y-2">
                <textarea
                  value={focusInput}
                  onChange={(event) => setFocusInput(event.target.value)}
                  rows={3}
                  placeholder="Or tell me in your own words what you want me to be great at."
                  className="w-full rounded-2xl px-3 py-3 text-sm"
                  style={{
                    border: "1px solid var(--c-border)",
                    backgroundColor: "var(--c-surface)",
                    color: "var(--c-text)",
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() =>
                    void handleFocusChoice(
                      "general",
                      focusInput.trim() || "General help",
                      focusInput,
                    )
                  }
                  disabled={!focusInput.trim()}
                >
                  Use this answer
                </Button>
              </div>
            </div>
          )}

          {currentSetupStep.id === "ai" && (
            <div className="max-w-3xl space-y-4">
              <p className="max-w-xl text-sm" style={{ color: "var(--c-text-secondary)" }}>
                {aiSetup?.requiresConnection
                  ? "Add your own AI now if you want the full experience. You can still keep going without it for now."
                  : "Everything is ready. You can add your own subscription or API key now, or do it later."}
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {AI_CHOICES.filter((choice) => choice.id !== "workspace_default").map((choice) => (
                  <ChoiceButton
                    key={choice.id}
                    label={choice.label}
                    description={choice.description}
                    active={currentAiMode === choice.id}
                    onClick={() => {
                      if (choice.id === "byo_subscription") {
                        handleSubscriptionMode();
                        return;
                      }
                      handleApiMode();
                    }}
                  />
                ))}
              </div>
              <button
                onClick={() => void handleManagedAi()}
                className="text-left px-3 py-2 rounded-xl text-xs transition-colors"
                style={{ color: "var(--c-text-faint)" }}
              >
                {aiSetup?.requiresConnection ? "Skip for now" : "Later"}
              </button>

              {currentAiMode === "byo_subscription" && (
                <div className="space-y-3">
                  <p className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    Pick the subscription you want me to use first.
                  </p>
                  <div className="grid gap-2 md:grid-cols-3">
                    {getStarterVisibleSubscriptionProviders(availableSubscriptions).map(
                      (provider) => (
                        <ChoiceButton
                          key={provider.id}
                          label={provider.label}
                          description={provider.description}
                          active={selectedSubscriptionProvider?.id === provider.id}
                          onClick={() => {
                            setSelectedSubscriptionProvider(provider);
                            setSubscriptionFlow(null);
                            setSubscriptionToken("");
                            setSubscriptionError("");
                          }}
                        />
                      ),
                    )}
                  </div>
                  {selectedSubscriptionProvider && (
                    <div
                      className="rounded-2xl p-4 max-w-xl"
                      style={{
                        border: "1px solid var(--c-border)",
                        backgroundColor: "var(--c-surface)",
                      }}
                    >
                      <div className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                        Connect {selectedSubscriptionProvider.label}
                      </div>
                      {selectedSubscriptionProvider.flowType === "token_paste" ? (
                        <div className="space-y-3 mt-3">
                          <input
                            type="password"
                            value={subscriptionToken}
                            onChange={(event) => setSubscriptionToken(event.target.value)}
                            placeholder="Paste your token"
                            className="w-full rounded-xl px-3 py-2 text-sm"
                            style={{
                              border: "1px solid var(--c-border)",
                              backgroundColor: "var(--c-surface-2)",
                              color: "var(--c-text)",
                            }}
                          />
                          <Button
                            onClick={() => void connectSelectedSubscription()}
                            disabled={subscriptionBusy || subscriptionToken.trim().length < 10}
                          >
                            {subscriptionBusy ? "Connecting..." : "Connect it"}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3 mt-3">
                          {!subscriptionFlow?.flowId ? (
                            <Button
                              onClick={() => void connectSelectedSubscription()}
                              disabled={subscriptionBusy}
                            >
                              {subscriptionBusy ? "Opening..." : "Open browser sign-in"}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => void finishSubscriptionFlow()}
                              disabled={subscriptionBusy}
                            >
                              {subscriptionBusy ? "Finishing..." : "I signed in"}
                            </Button>
                          )}
                        </div>
                      )}
                      {subscriptionError && (
                        <p className="text-xs mt-2" style={{ color: "var(--c-danger)" }}>
                          {subscriptionError}
                        </p>
                      )}
                      <button
                        onClick={() => void keepIncludedAiForNow()}
                        className="mt-3 text-xs underline"
                        style={{ color: "var(--c-text-secondary)" }}
                      >
                        {aiSetup?.requiresConnection ? "Skip for now" : "Later"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {currentAiMode === "byo_api" && (
                <div className="space-y-3">
                  <p className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    Pick the API key you want me to use first.
                  </p>
                  <div className="grid gap-2 md:grid-cols-3">
                    {getStarterApiKeyChoices().map((provider) => (
                      <ChoiceButton
                        key={provider.id}
                        label={provider.label}
                        description={provider.description}
                        active={selectedApiProvider?.id === provider.id}
                        onClick={() => {
                          setSelectedApiProvider(provider);
                          setApiFieldValues({});
                          setApiKeyError("");
                        }}
                      />
                    ))}
                  </div>
                  {selectedApiProvider && (
                    <div
                      className="rounded-2xl p-4 max-w-xl"
                      style={{
                        border: "1px solid var(--c-border)",
                        backgroundColor: "var(--c-surface)",
                      }}
                    >
                      <div className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                        Save {selectedApiProvider.label} key
                      </div>
                      <div className="mt-3 space-y-3">
                        {selectedApiProvider.fields.map((field) => (
                          <label key={field.id} className="block">
                            <span
                              className="text-xs font-medium"
                              style={{ color: "var(--c-text-secondary)" }}
                            >
                              {field.label}
                            </span>
                            <input
                              type={
                                field.secret ? "password" : field.kind === "url" ? "url" : "text"
                              }
                              value={apiFieldValues[field.id] ?? ""}
                              onChange={(event) =>
                                setApiFieldValues((current) => ({
                                  ...current,
                                  [field.id]: event.target.value,
                                }))
                              }
                              placeholder={field.placeholder}
                              className="mt-1 w-full rounded-xl px-3 py-2 text-sm"
                              style={{
                                border: "1px solid var(--c-border)",
                                backgroundColor: "var(--c-surface-2)",
                                color: "var(--c-text)",
                              }}
                            />
                            <p className="mt-1 text-xs" style={{ color: "var(--c-text-faint)" }}>
                              {field.helper}
                            </p>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          onClick={() => void saveSelectedApiKey()}
                          disabled={
                            apiKeyBusy ||
                            !hasCompleteStarterApiValues(selectedApiProvider, apiFieldValues)
                          }
                        >
                          {apiKeyBusy ? "Saving..." : "Save key"}
                        </Button>
                        <Button variant="secondary" onClick={() => void keepIncludedAiForNow()}>
                          {aiSetup?.requiresConnection ? "Skip for now" : "Later"}
                        </Button>
                      </div>
                      {apiKeyError && (
                        <p className="text-xs mt-2" style={{ color: "var(--c-danger)" }}>
                          {apiKeyError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {currentSetupStep.id === "accounts" && (
            <div className="max-w-3xl space-y-4">
              {!accountChoice && (
                <div className="grid gap-2 md:grid-cols-2 max-w-xl">
                  {ACCOUNT_CHOICES.map((choice) => (
                    <ChoiceButton
                      key={choice.id}
                      label={choice.label}
                      description={choice.description}
                      onClick={() => void handleAccountChoice(choice.id as "connect" | "later")}
                    />
                  ))}
                </div>
              )}

              {accountChoice === "connect" && (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    {getStarterAccountConnections().map((card) => {
                      const status = serviceStatuses[card.id] ?? { state: "idle" as const };
                      return (
                        <div
                          key={card.id}
                          className="rounded-2xl p-4"
                          style={{
                            border: "1px solid var(--c-border)",
                            backgroundColor: "var(--c-surface)",
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div
                                className="text-sm font-semibold"
                                style={{ color: "var(--c-text)" }}
                              >
                                {card.label}
                              </div>
                              <p
                                className="mt-1 text-xs"
                                style={{ color: "var(--c-text-secondary)" }}
                              >
                                {card.description}
                              </p>
                              <p
                                className="mt-1 text-[11px]"
                                style={{ color: "var(--c-text-faint)" }}
                              >
                                {card.detail}
                              </p>
                            </div>
                            <button
                              onClick={() =>
                                setExpandedAccount((current) =>
                                  current === card.id ? null : card.id,
                                )
                              }
                              className="px-3 py-1.5 rounded-lg text-xs font-medium"
                              style={{
                                border: "1px solid var(--c-border)",
                                color: "var(--c-text-secondary)",
                                backgroundColor: "var(--c-surface-2)",
                              }}
                            >
                              {status.state === "connected" ? "Review" : "Connect"}
                            </button>
                          </div>

                          <p
                            className="mt-3 text-xs"
                            style={{
                              color:
                                status.state === "error"
                                  ? "var(--c-danger)"
                                  : "var(--c-text-secondary)",
                            }}
                          >
                            {status.detail ||
                              (status.state === "connected"
                                ? "Connected"
                                : status.state === "working"
                                  ? "Working..."
                                  : "Not connected")}
                          </p>

                          {expandedAccount === card.id && (
                            <div className="mt-3">
                              {card.id === "agent-email" ? (
                                <div className="grid gap-3 md:grid-cols-3">
                                  <label className="text-xs">
                                    <span style={{ color: "var(--c-text-secondary)" }}>
                                      Provider
                                    </span>
                                    <select
                                      value={agentEmailService}
                                      onChange={(event) => setAgentEmailService(event.target.value)}
                                      className="mt-1 w-full rounded-xl px-3 py-2"
                                      style={{
                                        border: "1px solid var(--c-border)",
                                        backgroundColor: "var(--c-surface-2)",
                                        color: "var(--c-text)",
                                      }}
                                    >
                                      <option value="resend">Resend</option>
                                      <option value="sendgrid">SendGrid</option>
                                      <option value="smtp">SMTP</option>
                                    </select>
                                  </label>
                                  <label className="text-xs">
                                    <span style={{ color: "var(--c-text-secondary)" }}>
                                      From address
                                    </span>
                                    <input
                                      value={agentEmailAddress}
                                      onChange={(event) => setAgentEmailAddress(event.target.value)}
                                      placeholder="agent@yourdomain.com"
                                      className="mt-1 w-full rounded-xl px-3 py-2"
                                      style={{
                                        border: "1px solid var(--c-border)",
                                        backgroundColor: "var(--c-surface-2)",
                                        color: "var(--c-text)",
                                      }}
                                    />
                                  </label>
                                  <label className="text-xs">
                                    <span style={{ color: "var(--c-text-secondary)" }}>
                                      API key
                                    </span>
                                    <input
                                      type="password"
                                      value={agentEmailKey}
                                      onChange={(event) => setAgentEmailKey(event.target.value)}
                                      placeholder="Paste API key"
                                      className="mt-1 w-full rounded-xl px-3 py-2"
                                      style={{
                                        border: "1px solid var(--c-border)",
                                        backgroundColor: "var(--c-surface-2)",
                                        color: "var(--c-text)",
                                      }}
                                    />
                                  </label>
                                  <div className="md:col-span-3">
                                    <Button
                                      onClick={() => void saveAgentEmail()}
                                      disabled={
                                        agentEmailBusy ||
                                        !agentEmailKey.trim() ||
                                        !agentEmailAddress.trim()
                                      }
                                    >
                                      {agentEmailBusy ? "Saving..." : "Save agent email"}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => void connectAccount(card.id)}
                                  disabled={status.state === "working"}
                                >
                                  {status.state === "working" ? "Connecting..." : "Connect"}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {serviceError && (
                    <p className="text-xs" style={{ color: "var(--c-danger)" }}>
                      {serviceError}
                    </p>
                  )}
                  <Button variant="secondary" onClick={() => void continueAfterAccounts()}>
                    Continue
                  </Button>
                </>
              )}
            </div>
          )}

          {currentSetupStep.id === "budget" && (
            <div className="max-w-xl grid gap-2">
              {BUDGET_CHOICES.map((choice) => (
                <ChoiceButton
                  key={choice.id}
                  label={choice.label}
                  description={choice.description}
                  active={budgetSelection === choice.id}
                  onClick={() => void handleBudgetChoice(choice.id, choice.label)}
                />
              ))}
            </div>
          )}

          {currentSetupStep.id === "methodology" && (
            <div className="max-w-3xl space-y-5">
              <div>
                <div
                  className="text-xs font-semibold uppercase mb-2"
                  style={{ color: "var(--c-text-faint)" }}
                >
                  Do you batch tasks (work in big blocks) or interleave?
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  {METHODOLOGY_TASKING_CHOICES.map((choice) => (
                    <ChoiceButton
                      key={choice.id}
                      label={choice.label}
                      description={choice.description}
                      active={methodologyDraft.taskingStyle === choice.id}
                      onClick={() =>
                        setMethodologyDraft((current) => ({
                          ...current,
                          taskingStyle: choice.id as MethodologyTaskingStyle,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>

              <div>
                <div
                  className="text-xs font-semibold uppercase mb-2"
                  style={{ color: "var(--c-text-faint)" }}
                >
                  How many open projects feel manageable to you?
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  {METHODOLOGY_PROJECT_CAPACITY_CHOICES.map((choice) => (
                    <ChoiceButton
                      key={choice.id}
                      label={choice.label}
                      description={choice.description}
                      active={methodologyDraft.projectCapacity === choice.id}
                      onClick={() =>
                        setMethodologyDraft((current) => ({
                          ...current,
                          projectCapacity: choice.id as MethodologyProjectCapacity,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>

              <div>
                <div
                  className="text-xs font-semibold uppercase mb-2"
                  style={{ color: "var(--c-text-faint)" }}
                >
                  Do you do weekly reviews?
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  {METHODOLOGY_REVIEW_CADENCE_CHOICES.map((choice) => (
                    <ChoiceButton
                      key={choice.id}
                      label={choice.label}
                      description={choice.description}
                      active={methodologyDraft.weeklyReviewCadence === choice.id}
                      onClick={() =>
                        setMethodologyDraft((current) => ({
                          ...current,
                          weeklyReviewCadence: choice.id as MethodologyReviewCadence,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>

              {methodologyError && (
                <p className="text-xs" style={{ color: "var(--c-danger)" }}>
                  {methodologyError}
                </p>
              )}

              <Button
                onClick={() => void submitMethodologyAnswers()}
                disabled={
                  methodologyBusy ||
                  !methodologyDraft.taskingStyle ||
                  !methodologyDraft.projectCapacity ||
                  !methodologyDraft.weeklyReviewCadence
                }
              >
                {methodologyBusy ? "Saving..." : "Continue"}
              </Button>
            </div>
          )}
        </div>
      )}

      {phase === "tour" && (
        <div className="pl-4 md:pl-10 max-w-2xl space-y-3">
          <div
            className="rounded-2xl p-4"
            style={{ border: "1px solid var(--c-border)", backgroundColor: "var(--c-surface)" }}
          >
            <div className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
              {currentTourStep.title}
            </div>
            <p className="mt-2 text-sm" style={{ color: "var(--c-text-secondary)" }}>
              {currentTourStep.body}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => void advanceTour()}>{currentTourStep.actionLabel}</Button>
              {currentTourStep.surface === "settings" && (
                <Button
                  variant="secondary"
                  onClick={() => onOpenSettings?.({ sectionId: "connect", pageId: "ai" })}
                >
                  Open AI setup
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs" style={{ color: "var(--c-text-faint)" }}>
            Step {tourIndex + 1} of {GUIDED_TOUR_STEPS.length}
          </p>
        </div>
      )}

      {phase === "complete" && (
        <div className="pl-4 md:pl-10 space-y-3">
          <p className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
            Setup is done. Start with a real task and I’ll help shape the workspace around it.
          </p>
          <Button onClick={() => void finishOnboarding()} disabled={finishing}>
            {finishing ? "Saving..." : "Start chatting"}
          </Button>
        </div>
      )}

      <div ref={scrollRef} />
    </div>
  );
}
